/**
 * A leitura única do payload do dashboard.
 *
 * POR QUE EXISTE: sete telas resolvendo "onde mora o número da margem líquida"
 * são sete lugares para divergir, e foi exatamente assim que este produto
 * acabou com quatro pares de indicadores exibindo o mesmo número com nomes
 * diferentes. Aqui a resolução acontece uma vez, e as telas consomem.
 *
 * REGRA QUE NÃO SE NEGOCIA: nada aqui usa `Number(x) || 0`. Toda razão derivada
 * passa por `combinar`, que devolve `null` quando qualquer lado falta. Um `|| 0`
 * transformaria "não informado" em "medi e deu zero" — e em financeiro zero é
 * uma afirmação sobre a empresa.
 */

import {
  valoresDaConta,
  quantidadeDePeriodos,
  ultimoValor,
  somarConhecidos,
  coberturaDoDRE,
  numeroOuNulo,
  combinar,
  alinhar
} from '../utils/series';
import { rotulosDoDRE } from '../utils/periodos';

/** Razão em percentual que propaga lacuna e recusa base zero. */
const percentualDe = (serie, base) =>
  combinar(serie, base, (v, b) => (b > 0 ? (v / b) * 100 : null));

/** Série a partir de qualquer forma que o payload use. */
const serieDe = (bruto, periodos) => valoresDaConta(bruto, periodos);

/**
 * Em que posição da janela EXIBIDA começa a janela CALCULADA.
 *
 * Existem duas janelas desde a onda 3: a tela mostra a série inteira, com os
 * buracos visíveis, e o motor calcula sobre o maior trecho contíguo. Quando há
 * lacuna, a série de indicadores é MAIS CURTA que o eixo da tela — e encaixá-la
 * a partir da primeira coluna faria a margem de junho ser desenhada em janeiro.
 *
 * O deslocamento é descoberto pela competência, nunca pelo tamanho: o trecho
 * calculado termina no último mês COM DRE, que nem sempre é o último mês do
 * eixo exibido.
 */
export const deslocamentoDoCalculo = (dre, competenciasDoCalculo) => {
  if (!Array.isArray(competenciasDoCalculo) || !competenciasDoCalculo.length) return 0;
  const eixo = (dre?.periodos || []).map((p) => p?.competencia);
  const inicio = eixo.indexOf(competenciasDoCalculo[0]);
  return inicio > 0 ? inicio : 0;
};

/** Escalar do payload, preservando os três estados. */
const escalarDe = (valor) => numeroOuNulo(valor);

/**
 * Lê tudo que as sete telas precisam de um payload de dashboard.
 *
 * @param {object} dashboardData resposta de `/api/upload/generate-dashboard`
 * @returns {object|null}
 */
export const lerDiagnostico = (dashboardData) => {
  if (!dashboardData) return null;

  const dre = dashboardData.dre || null;
  const balanco = dashboardData.balanco || null;
  const adicionais = dashboardData.indicadoresAdicionais || {};

  const periodos = quantidadeDePeriodos(dre);
  const rotulos = rotulosDoDRE(dre);
  const cobertura = coberturaDoDRE(dre);
  const temBalanco = Boolean(balanco);

  const ind = dre?.indicadores || {};
  const faturamento = serieDe(ind.faturamento?.valores, periodos);
  const receitaLiquida = serieDe(ind.receitaLiquida?.valores, periodos);
  const cmv = serieDe(ind.cmv?.valores, periodos);
  const custosFixos = serieDe(ind.custosFixos?.valores, periodos);
  const custosVariaveis = serieDe(ind.custosVariaveis?.valores, periodos);
  const custoTotalReal = serieDe(ind.custoTotalReal?.valores, periodos);
  const compraMercadoria = serieDe(ind.compraMercadoria?.valores, periodos);
  const pontoEquilibrio = serieDe(ind.pontoEquilibrio?.valores, periodos);
  const lucroCaixa = serieDe(ind.lucro?.real?.valores, periodos);
  const lucroCompetencia = serieDe(ind.lucro?.competencia?.valores, periodos);
  const despesasFinanceiras = serieDe(ind.despesasFinanceiras?.valores, periodos);

  // Os indicadores adicionais são calculados sobre o trecho contíguo final e
  // precisam ser encaixados na posição certa do eixo exibido.
  const deslocamento = deslocamentoDoCalculo(dre, dashboardData._competenciasDoCalculo);
  const doAdicional = (chave) => {
    const serie = valoresDaConta(adicionais[chave]);
    return alinhar(deslocamento ? [...Array(deslocamento).fill(null), ...serie] : serie, periodos);
  };

  // O balanço mede um instante e não tem série: os campos abaixo são escalares
  // colados no período mais recente, e a tela diz a que data eles se referem.
  const ativoCirculante = balanco?.ativo?.circulante || {};
  const passivoCirculante = balanco?.passivo?.circulante || {};
  const passivoNaoCirculante = balanco?.passivo?.naoCirculante || {};

  const disponibilidades = temBalanco
    ? (escalarDe(ativoCirculante.caixa) ?? 0)
      + (escalarDe(ativoCirculante.bancos) ?? 0)
      + (escalarDe(ativoCirculante.aplicacoes) ?? 0)
    : null;
  const acTotal = escalarDe(ativoCirculante.total);
  const pcTotal = escalarDe(passivoCirculante.total);
  const estoque = escalarDe(ativoCirculante.estoque);
  const ativoTotal = escalarDe(balanco?.ativo?.total);
  const passivoTotal = escalarDe(balanco?.passivo?.total);
  const pncTotal = escalarDe(passivoNaoCirculante.total);

  const razao = (numerador, denominador) =>
    numerador === null || denominador === null || !(denominador > 0)
      ? null
      : numerador / denominador;

  // O questionário só emite liquidez corrente e endividamento; a planilha emite
  // as três liquidezes. Derivar as que faltam da estrutura evita que metade dos
  // usuários veja "não informado" num número que o dado dela já responde.
  const liquidezCorrente = escalarDe(balanco?.indicadores?.liquidezCorrente) ?? razao(acTotal, pcTotal);
  const liquidezSeca = escalarDe(balanco?.indicadores?.liquidezSeca)
    ?? (acTotal !== null && estoque !== null ? razao(acTotal - estoque, pcTotal) : null);
  const liquidezImediata = escalarDe(balanco?.indicadores?.liquidezImediata) ?? razao(disponibilidades, pcTotal);

  const endividamentoFracao = escalarDe(balanco?.indicadores?.endividamento) ?? razao(passivoTotal, ativoTotal);
  const dividaTotal = pcTotal !== null && pncTotal !== null ? pcTotal + pncTotal : null;

  const receitaAcumulada = somarConhecidos(receitaLiquida);
  const receitaRecente = ultimoValor(receitaLiquida);

  const dso = escalarDe(adicionais.dso);
  const dio = escalarDe(adicionais.dio);

  const series = {
    faturamento,
    receitaLiquida,
    cmv,
    custosFixos,
    custosVariaveis,
    custoTotalReal,
    compraMercadoria,
    pontoEquilibrio,
    lucroCaixa,
    lucroCompetencia,
    despesasFinanceiras,
    crescimentoMoM: doAdicional('crescimentoMoM'),
    margemBruta: doAdicional('margemBruta'),
    margemContribuicao: doAdicional('margemContribuicao'),
    margemLiquida: doAdicional('margemLiquida'),
    margemLiquidaCompetencia: doAdicional('margemLiquidaCompetencia'),
    margemSeguranca: doAdicional('margemSeguranca'),
    ebitdaProxy: doAdicional('ebitdaProxy'),
    icr: doAdicional('icr'),
    dscr: doAdicional('dscr'),
    pessoalSobreReceita: doAdicional('pessoalSobreReceita'),
    marketingSobreReceita: doAdicional('marketingSobreReceita'),
    ccmSobreReceita: doAdicional('ccmSobreReceita'),
    fornecedorTop1Share: doAdicional('fornecedorTop1Share'),
    // Derivadas aqui porque o motor as calcula sobre faturamento bruto, e a
    // comparação com benchmark exige receita líquida no denominador.
    custosFixosSobreReceita: percentualDe(custosFixos, receitaLiquida),
    custosVariaveisSobreReceita: percentualDe(custosVariaveis, receitaLiquida),
    jurosSobreReceita: percentualDe(despesasFinanceiras, receitaLiquida)
  };

  const valores = {
    receitaLiquida: receitaRecente,
    faturamento: ultimoValor(faturamento),
    pontoEquilibrio: ultimoValor(pontoEquilibrio),
    ebitdaProxy: ultimoValor(series.ebitdaProxy),
    margemBruta: ultimoValor(series.margemBruta),
    margemContribuicao: ultimoValor(series.margemContribuicao),
    margemLiquida: ultimoValor(series.margemLiquida),
    margemLiquidaCompetencia: ultimoValor(series.margemLiquidaCompetencia),
    margemSeguranca: ultimoValor(series.margemSeguranca),
    custosFixosSobreReceita: ultimoValor(series.custosFixosSobreReceita),
    pessoalSobreReceita: ultimoValor(series.pessoalSobreReceita),
    marketingSobreReceita: ultimoValor(series.marketingSobreReceita),
    ccmSobreReceita: ultimoValor(series.ccmSobreReceita),
    fornecedorTop1Share: ultimoValor(series.fornecedorTop1Share),

    liquidezCorrente,
    liquidezSeca,
    liquidezImediata,
    capitalDeGiro: escalarDe(adicionais.capitalDeGiro),
    cashToDebt: escalarDe(adicionais.cashToDebt),
    runwayMeses: escalarDe(adicionais.runwayMeses),

    dso,
    dio,
    dpo: escalarDe(adicionais.dpo),
    // Ciclo operacional = recebimento + estoque. Falta de um lado apaga o total:
    // somar só o que existe daria um ciclo curto por falta de dado.
    cicloOperacional: dso === null || dio === null ? null : dso + dio,
    ccc: escalarDe(adicionais.ccc),
    ncg: escalarDe(adicionais.ncg),
    saldoTesouraria: escalarDe(adicionais.saldoTesouraria),

    despesasFinanceiras: ultimoValor(despesasFinanceiras),
    jurosSobreReceita: ultimoValor(series.jurosSobreReceita),
    icr: ultimoValor(series.icr),
    dscr: ultimoValor(series.dscr),

    endividamento: endividamentoFracao === null ? null : endividamentoFracao * 100,
    composicaoEndividamento: razao(pcTotal, dividaTotal) === null ? null : razao(pcTotal, dividaTotal) * 100,
    alavancagemFinanceira: escalarDe(adicionais.alavancagemFinanceira),
    // Giro do ativo com a receita ACUMULADA da janela exibida — a perna que
    // faltava do DuPont. A tela nomeia o período para o número não ser lido
    // como anual.
    giroDoAtivo: razao(receitaAcumulada, ativoTotal),
    roe: escalarDe(adicionais.roe),
    roa: escalarDe(adicionais.roa)
  };

  return {
    dre,
    balanco,
    adicionais,
    temBalanco,
    periodos,
    rotulos,
    cobertura,
    // A que data os saldos do balanço se referem. Um cliente entrega balancete
    // fechado em 31/01 e outro entrega a posição do dia 20: guardar só "janeiro"
    // apaga a diferença e depois ninguém explica por que dois clientes do mesmo
    // mês têm bases diferentes.
    referenciaDoBalanco: dashboardData._referenciaDoBalanco || null,
    series,
    valores,
    receitaAcumulada,
    disponibilidades,
    // O motor já nomeia o que falta por indicador. A tela repete o motivo dele
    // em vez de inventar um texto próprio que vai divergir.
    motivos: adicionais.meta || {},
    indiceSaude: adicionais.indiceSaude || null,
    // O gate de LLM vem junto do parecer desde a onda 2: a tela precisa exibir
    // o MOTIVO da recusa, não um silêncio.
    llm: dashboardData.consultantInsights?.llm || null,
    alertas: dashboardData.alertas || null,
    insights: dashboardData.consultantInsights || null
  };
};

/**
 * Motivo de um indicador estar vazio.
 *
 * O motor devolve a LISTA do que falta, em rótulos que são complemento de
 * "Requer ..." — a frase é montada aqui. Antes o rótulo ia cru para a tela, e a
 * coluna exibia "Requer o balanço patrimonial" logo acima de "a lista de
 * fornecedores, conta a conta", um fragmento sem verbo que lê como texto
 * truncado.
 *
 * Nem toda ausência é falta de dado: runway de caixa some porque a empresa deu
 * lucro e não há queima a contar. Esse caso chega como `{ naoSeAplica }` e traz
 * a frase pronta — travessão sem explicação nenhuma parece defeito do produto.
 */
export const motivoDaAusencia = (leitura, chave, exigeBalanco) => {
  const doMotor = leitura?.motivos?.[chave];
  if (Array.isArray(doMotor) && doMotor.length) return `Requer ${doMotor.join(', ')}`;
  if (doMotor && typeof doMotor === 'object' && doMotor.naoSeAplica) return doMotor.naoSeAplica;
  if (exigeBalanco && !leitura?.temBalanco) return 'Requer o balanço patrimonial';
  return null;
};
